import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import AdminSidebar from '../../../components/AdminSidebar/AdminSidebar';
import { FaCheckCircle, FaTimesCircle, FaTrash, FaEye, FaChevronDown, FaChevronRight, FaCheckDouble, FaBan, FaInfoCircle, FaUser, FaBook, FaTimes, FaDiscord, FaCalendarAlt, FaStar, FaCompass, FaHistory } from 'react-icons/fa'; // Iconos extra para modal/sections
import { AuthContext } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../config/api';
import './AdminWhitelist.css';

// Componente auxiliar para mostrar detalles en el modal
const DetailItem = ({ label, value, fullWidth = false, isBlock = false }) => (
	<div className={`detail-item ${fullWidth ? 'full' : ''} ${isBlock ? 'block' : ''}`}>
		<span className="detail-label">{label}:</span>
		<span className="detail-value">{value || <span style={{color:'#bbb'}}>N/D</span>}</span>
	</div>
);

const AdminWhitelist = () => {
  const { authToken } = useContext(AuthContext);
  const [whitelistRequests, setWhitelistRequests] = useState([]);
  const [isAddWhitelistModalOpen, setIsAddWhitelistModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null); 
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  
  const [newRequest, setNewRequest] = useState({
    username: '',
    discord_id: '',
    ooc_edad: '',
    ooc_experiencia_rol: '',
    ic_aptitudes_pj: '',
    ic_futuro_pj: '',
    ic_historia_pj: '',
    status: 'pending',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [viewMode, setViewMode] = useState('table'); // 'table' o 'cards'
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const STATUS_LABELS = useMemo(() => ({ pending: 'Pendiente', approved: 'Aprobado', rejected: 'Rechazado' }), []);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [sort, setSort] = useState({ field: 'created_at', dir: 'desc' });

  const fetchWhitelistRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/whitelistRequests`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }
      const data = await response.json();
      setWhitelistRequests(data);
    } catch (err) {
      setError('Error al cargar solicitudes de whitelist: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchWhitelistRequests();
  }, [fetchWhitelistRequests]);

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setIsViewModalOpen(true);
  };

  const handleNewRequestInputChange = (e) => {
    const { name, value } = e.target;
    setNewRequest(prev => ({ ...prev, [name]: value }));
  };

  const handleAddRequest = async (e) => {
    e.preventDefault();
    if (!newRequest.username.trim() || !newRequest.discord_id.trim()) {
      alert('Username y Discord ID son obligatorios.');
      return;
    }
    // Validación de duplicados (advertencia no bloqueante)
    const usernameExists = whitelistRequests.some(r => r.username.trim().toLowerCase() === newRequest.username.trim().toLowerCase());
    const discordExists = whitelistRequests.some(r => r.discord_id.trim() === newRequest.discord_id.trim());
    if (usernameExists || discordExists) {
      const proceed = window.confirm('Ya existe una solicitud registrada con este nombre de usuario o Discord ID. ¿Deseas añadir esta nueva solicitud de todas formas?');
      if (!proceed) return;
    }
    try {
      const requestToSend = {
        ...newRequest,
        created_at: new Date().toISOString(),
      };
      const response = await fetch(`${API_BASE_URL}/whitelistRequests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestToSend),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || response.statusText);
      }
      await fetchWhitelistRequests();
      setNewRequest({
        username: '',
        discord_id: '',
        ooc_edad: '',
        ooc_experiencia_rol: '',
        ic_aptitudes_pj: '',
        ic_futuro_pj: '',
        ic_historia_pj: '',
        status: 'pending',
      });
      setIsAddWhitelistModalOpen(false);
      alert('Solicitud añadida con éxito.');
    } catch (err) {
      setError('Error al añadir solicitud: ' + err.message);
      console.error(err);
    }
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    if (!window.confirm(`¿Seguro que quieres ${newStatus === 'approved' ? 'aprobar' : (newStatus === 'rejected' ? 'rechazar' : 'eliminar')} esta solicitud?`)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/whitelistRequests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || response.statusText);
      }

      await fetchWhitelistRequests();
      alert(`Solicitud ${newStatus} con éxito.`);
    } catch (err) {
      setError(`Error al actualizar estado: ${err.message}`);
      console.error(err);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('¿Seguro que quieres eliminar esta solicitud?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/whitelistRequests/${requestId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || response.statusText);
      }
      setWhitelistRequests(prev => prev.filter(r => r.id !== requestId));
      alert('Solicitud eliminada con éxito.');
    } catch (err) {
      setError('Error al eliminar solicitud: ' + err.message);
      console.error(err);
    }
  };

  const filteredWhitelistRequests = whitelistRequests.filter((req) => {
    const usernameStr = req.username || '';
    const discordIdStr = req.discord_id || '';
    const matchesSearch =
      usernameStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      discordIdStr.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ? true : req.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const sortedWhitelistRequests = useMemo(() => {
    const copy = [...filteredWhitelistRequests];
    const { field, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      const va = a?.[field];
      const vb = b?.[field];
      if (field === 'created_at' || field === 'updated_at' || field === 'ooc_edad' || field === 'id') {
        const na = va == null ? -Infinity : (field === 'ooc_edad' ? Number(va) : new Date(va).getTime());
        const nb = vb == null ? -Infinity : (field === 'ooc_edad' ? Number(vb) : new Date(vb).getTime());
        return (na - nb) * mul;
      }
      if (field === 'status') {
        const order = { approved: 2, pending: 1, rejected: 0 };
        return ((order[va] ?? -1) - (order[vb] ?? -1)) * mul;
      }
      const sa = (va ?? '').toString().toLowerCase();
      const sb = (vb ?? '').toString().toLowerCase();
      if (sa < sb) return -1 * mul;
      if (sa > sb) return 1 * mul;
      return 0;
    });
    return copy;
  }, [filteredWhitelistRequests, sort]);

  const totalPages = Math.ceil(sortedWhitelistRequests.length / perPage) || 1;
  const paginatedRequests = sortedWhitelistRequests.slice((page - 1) * perPage, page * perPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = (checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        paginatedRequests.forEach(r => next.add(r.id));
      } else {
        paginatedRequests.forEach(r => next.delete(r.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (field) => {
    setSort(prev => {
      if (prev.field === field) {
        return { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { field, dir: 'asc' };
    });
  };

  const bulkUpdateStatus = async (newStatus) => {
    if (selectedIds.size === 0) return;
    const label = newStatus === 'approved' ? 'aprobar' : newStatus === 'rejected' ? 'rechazar' : 'actualizar';
    if (!window.confirm(`¿Seguro que quieres ${label} ${selectedIds.size} solicitudes seleccionadas?`)) return;
    try {
      await Promise.allSettled(Array.from(selectedIds).map(id => fetch(`${API_BASE_URL}/whitelistRequests/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ status: newStatus })
      })));
      await fetchWhitelistRequests();
      clearSelection();
      alert(`Solicitudes ${label} con éxito.`);
    } catch (err) {
      console.error(err);
      alert('Ocurrió un error procesando la acción masiva.');
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`¿Seguro que quieres eliminar ${selectedIds.size} solicitudes seleccionadas? Esta acción no se puede deshacer.`)) return;
    try {
      await Promise.allSettled(Array.from(selectedIds).map(id => fetch(`${API_BASE_URL}/whitelistRequests/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      })));
      await fetchWhitelistRequests();
      clearSelection();
      alert('Solicitudes eliminadas con éxito.');
    } catch (err) {
      console.error(err);
      alert('Ocurrió un error eliminando algunas solicitudes.');
    }
  };

  return (
    <div className="admin-users-container">
      <AdminSidebar />
      <div className="admin-content">
        <h1 className="admin-page-title">Gestión de Solicitudes Whitelist</h1>
        <div style={{display:'flex',gap:16,alignItems:'center',marginBottom:16,flexWrap:'wrap'}}>
          <label style={{fontWeight:500}}>Ver como:</label>
          <select value={viewMode} onChange={e => setViewMode(e.target.value)} style={{padding:4}}>
            <option value="table">Tabla</option>
            <option value="cards">Tarjetas</option>
          </select>
          <label style={{fontWeight:500}}>Mostrar:</label>
          <select value={perPage} onChange={e => {setPerPage(Number(e.target.value));setPage(1);}} style={{padding:4}}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <span style={{color:'#888',fontSize:'0.95rem'}}>Página {page} de {totalPages}</span>
        </div>
        {loading && (
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',margin:'40px 0'}}>
            <div className="modern-spinner" />
            <span style={{color:'#888',fontWeight:500,fontSize:'1.1rem',marginTop:12}}>Cargando solicitudes...</span>
          </div>
        )}
        {error && <p className="error-message">{error}</p>}
				
        <section className="admin-section users-list-section">
          <div className="list-header-controls" style={{gap: 10, flexWrap: 'wrap'}}>
            <input
              type="text"
              placeholder="Buscar por usuario o Discord..."
              value={searchTerm}
              onChange={e => {setSearchTerm(e.target.value); setPage(1);}}
              className="search-input"
              style={{minWidth: 200}}
            />
            <select
              value={statusFilter}
              onChange={e => {setStatusFilter(e.target.value); setPage(1);}}
              className="search-input"
              style={{maxWidth: 180}}
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="approved">Aprobados</option>
              <option value="rejected">Suspendidos/Rechazados</option>
            </select>
            <button
              className="action-button primary-button add-new-user-button"
              onClick={() => setIsAddWhitelistModalOpen(true)}
            >
              Añadir Solicitud
            </button>
          </div>
					{viewMode === 'table' && selectedIds.size > 0 && (
						<div className="bulk-actions-bar">
							<span>{selectedIds.size} seleccionada(s)</span>
							<div className="bulk-actions">
								<button className="action-button icon-button success-button" onClick={() => bulkUpdateStatus('approved')} title="Aprobar seleccionadas"><FaCheckDouble /></button>
								<button className="action-button icon-button danger-button" onClick={() => bulkUpdateStatus('rejected')} title="Rechazar seleccionadas"><FaBan /></button>
								<button className="action-button icon-button delete-button" onClick={bulkDelete} title="Eliminar seleccionadas"><FaTrash /></button>
								<button className="action-button secondary-button" onClick={clearSelection} title="Limpiar selección">Limpiar</button>
							</div>
						</div>
					)}
          
		  {viewMode === 'table' ? (
						<div className="table-responsive">
							<table className="users-table">
								<thead>
									<tr>
										<th className="select-col">
											<input
												type="checkbox"
												aria-label="Seleccionar todos"
												onChange={(e) => selectAllVisible(e.target.checked)}
												checked={paginatedRequests.length > 0 && paginatedRequests.every(r => selectedIds.has(r.id))}
											/>
										</th>
										<th className="expand-col" />
										<th onClick={() => handleSort('id')} className={`sortable ${sort.field==='id' ? sort.dir : ''}`}>ID</th>
										<th onClick={() => handleSort('username')} className={`sortable ${sort.field==='username' ? sort.dir : ''}`}>Usuario</th>
										<th onClick={() => handleSort('discord_id')} className={`sortable ${sort.field==='discord_id' ? sort.dir : ''}`}>Discord ID</th>
										<th onClick={() => handleSort('ooc_edad')} className={`sortable ${sort.field==='ooc_edad' ? sort.dir : ''}`}>OOC Edad</th>
										<th onClick={() => handleSort('status')} className={`sortable ${sort.field==='status' ? sort.dir : ''}`}>Estado</th>
										<th onClick={() => handleSort('created_at')} className={`sortable ${sort.field==='created_at' ? sort.dir : ''}`}>F. Creación</th>
										<th onClick={() => handleSort('updated_at')} className={`sortable ${sort.field==='updated_at' ? sort.dir : ''}`}>F. Actualización</th>
										<th>Acciones</th>
									</tr>
								</thead>
								<tbody>
									{paginatedRequests.length > 0 ? (
										paginatedRequests.map(request => (
											<React.Fragment key={request.id}>
												<tr className={`${expandedIds.has(request.id) ? 'expanded' : ''} ${selectedIds.has(request.id) ? 'selected' : ''}`}>
													<td className="select-col">
														<input type="checkbox" aria-label={`Seleccionar ${request.id}`} checked={selectedIds.has(request.id)} onChange={() => toggleSelect(request.id)} />
													</td>
													<td className="expand-col">
														<button className="expand-button" onClick={() => toggleExpand(request.id)} aria-label={expandedIds.has(request.id) ? 'Contraer' : 'Expandir'}>
															{expandedIds.has(request.id) ? <FaChevronDown /> : <FaChevronRight />}
														</button>
													</td>
													<td>{request.id ?? '-'}</td>
													<td>{request.username || <span style={{color:'#bbb'}}>Sin usuario</span> }</td>
													<td>{request.discord_id || <span style={{color:'#bbb'}}>Sin Discord</span>}</td>
													<td>{request.ooc_edad || <span style={{color:'#bbb'}}>N/D</span>}</td>
													<td>
														<span className={`status-badge status-${request.status}`}>{request.status ? (STATUS_LABELS[request.status] || request.status) : '-'}</span>
													</td>
													<td>{request.created_at ? new Date(request.created_at).toLocaleString('es-ES', { hour12: false }) : '-'}</td>
													<td>{request.updated_at ? new Date(request.updated_at).toLocaleString('es-ES', { hour12: false }) : '-'}</td>
													<td>
														<div className="action-buttons">
															<button onClick={() => handleViewRequest(request)} className="action-button icon-button view-button" title="Ver Detalles"><FaEye /></button>
															<button onClick={() => updateRequestStatus(request.id, 'approved')} className="action-button icon-button success-button" title="Aprobar"><FaCheckCircle /></button>
															<button onClick={() => updateRequestStatus(request.id, 'rejected')} className="action-button icon-button danger-button" title="Rechazar"><FaTimesCircle /></button>
															<button onClick={() => handleDeleteRequest(request.id)} className="action-button icon-button delete-button" title="Eliminar"><FaTrash /></button>
														</div>
													</td>
												</tr>
												{expandedIds.has(request.id) && (
													<tr className="whitelist-row-details">
														<td colSpan={10}>
															<div className="row-details">
																<div className="details-grid">
																	<div className="detail"><b>OOC Experiencia:</b><div className="long-text">{request.ooc_experiencia_rol || <span style={{color:'#bbb'}}>N/D</span>}</div></div>
																	<div className="detail"><b>IC Aptitudes:</b><div className="long-text">{request.ic_aptitudes_pj || <span style={{color:'#bbb'}}>N/D</span>}</div></div>
																	<div className="detail"><b>IC Futuro:</b><div className="long-text">{request.ic_futuro_pj || <span style={{color:'#bbb'}}>N/D</span>}</div></div>
																	<div className="detail"><b>IC Historia:</b><div className="long-text">{request.ic_historia_pj || <span style={{color:'#bbb'}}>N/D</span>}</div></div>
																</div>
															</div>
														</td>
													</tr>
												)}
											</React.Fragment>
										))
									) : (
										<tr>
											<td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
												No se encontraron solicitudes de whitelist.
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					) : (
						<div className="whitelist-cards-container">
							{paginatedRequests.length > 0 ? (
								paginatedRequests.map(request => (
									<div key={request.id} className={`whitelist-card status-${request.status}`}>
										<div className="card-header">
											<div>
												<span style={{fontWeight:600,fontSize:'1.05rem'}}>{request.username || <span style={{color:'#bbb'}}>Sin usuario</span>}</span>
												<div className="card-id">ID: {request.id}</div>
											</div>
											<span className={`status-badge status-${request.status}`}>
												{request.status ? request.status.charAt(0).toUpperCase() + request.status.slice(1) : '-'}
											</span>
										</div>
										<div className="card-section">
											<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
												<div><b>Discord:</b> <span className="long-text">{request.discord_id || <span style={{color:'#bbb'}}>Sin Discord</span>}</span></div>
												<div><b>Edad:</b> <span className="long-text">{request.ooc_edad || <span style={{color:'#bbb'}}>N/D</span>}</span></div>
												<div style={{gridColumn:'1 / -1',fontSize:'0.85rem',color:'#9aa0a6'}}>
													Creado: {request.created_at ? new Date(request.created_at).toLocaleString() : '-'} · Actualizado: {request.updated_at ? new Date(request.updated_at).toLocaleString() : '-'}
												</div>
											</div>
											<div><b>OOC Experiencia:</b><div className="long-text">{request.ooc_experiencia_rol || <span style={{color:'#bbb'}}>N/D</span>}</div></div>
											<div><b>IC Aptitudes:</b><div className="long-text">{request.ic_aptitudes_pj || <span style={{color:'#bbb'}}>N/D</span>}</div></div>
											<div><b>IC Futuro:</b><div className="long-text">{request.ic_futuro_pj || <span style={{color:'#bbb'}}>N/D</span>}</div></div>
											<div><b>IC Historia:</b><div className="long-text">{request.ic_historia_pj || <span style={{color:'#bbb'}}>N/D</span>}</div></div>
										</div>
										<div className="action-buttons">
											<button onClick={() => handleViewRequest(request)} className="action-button icon-button view-button" title="Ver Detalles">
												<FaEye size={16} />
											</button>
											<button onClick={() => updateRequestStatus(request.id, 'approved')} className="action-button icon-button success-button" title="Aprobar">
												<FaCheckCircle size={16} />
											</button>
											<button onClick={() => updateRequestStatus(request.id, 'rejected')} className="action-button icon-button danger-button" title="Rechazar">
												<FaTimesCircle size={16} />
											</button>
											<button onClick={() => handleDeleteRequest(request.id)} className="action-button icon-button delete-button" title="Eliminar">
												<FaTrash size={16} />
											</button>
										</div>
									</div>
								))
							) : (
								<div style={{width:'100%',textAlign:'center',color:'#888',padding:'40px 0'}}>No se encontraron solicitudes de whitelist.</div>
							)}
						</div>
					)}
          {!loading && !error && filteredWhitelistRequests.length > 0 && (
            <div style={{display:'flex',gap:8,justifyContent:'center',alignItems:'center',margin:'32px 0'}}>
              <button onClick={()=>handlePageChange(page-1)} disabled={page===1} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #FFD700',background:page===1?'#eee':'#FFD700',color:page===1?'#aaa':'#222',fontWeight:600,cursor:page===1?'not-allowed':'pointer'}}>Anterior</button>
              <span style={{fontWeight:500}}>Página {page} de {totalPages}</span>
              <button onClick={()=>handlePageChange(page+1)} disabled={page===totalPages} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #FFD700',background:page===totalPages?'#eee':'#FFD700',color:page===totalPages?'#aaa':'#222',fontWeight:600,cursor:page===totalPages?'not-allowed':'pointer'}}>Siguiente</button>
            </div>
          )}
        </section>

        {/* Modal para añadir solicitud */}
        {isAddWhitelistModalOpen && (
          <div className="modal-overlay" onClick={() => setIsAddWhitelistModalOpen(false)}>
            <div className="modal-content whitelist-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="whitelist-modal-title">Añadir Nueva Solicitud Whitelist</h2>
              <form onSubmit={handleAddRequest} className="modal-form">
                <div className="form-group">
                  <label htmlFor="username">Usuario:</label>
                  <div className="whitelist-input-group">
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={newRequest.username}
                      onChange={handleNewRequestInputChange}
                      required
                    />
                    <FaUser className="whitelist-input-icon" />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="discord_id">Discord ID:</label>
                  <div className="whitelist-input-group">
                    <input
                      type="text"
                      id="discord_id"
                      name="discord_id"
                      value={newRequest.discord_id}
                      onChange={handleNewRequestInputChange}
                      required
                    />
                    <FaDiscord className="whitelist-input-icon" />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="ooc_edad">OOC Edad:</label>
                  <div className="whitelist-input-group">
                    <input
                      type="text"
                      id="ooc_edad"
                      name="ooc_edad"
                      value={newRequest.ooc_edad}
                      onChange={handleNewRequestInputChange}
                      required
                    />
                    <FaCalendarAlt className="whitelist-input-icon" />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="ooc_experiencia_rol">OOC Experiencia Rol:</label>
                  <div className="whitelist-input-group">
                    <textarea
                      id="ooc_experiencia_rol"
                      name="ooc_experiencia_rol"
                      value={newRequest.ooc_experiencia_rol}
                      onChange={handleNewRequestInputChange}
                      rows="3"
                      required
                    />
                    <FaBook className="whitelist-input-icon textarea-icon" />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="ic_aptitudes_pj">IC Aptitudes PJ:</label>
                  <div className="whitelist-input-group">
                    <textarea
                      id="ic_aptitudes_pj"
                      name="ic_aptitudes_pj"
                      value={newRequest.ic_aptitudes_pj}
                      onChange={handleNewRequestInputChange}
                      rows="3"
                      required
                    />
                    <FaStar className="whitelist-input-icon textarea-icon" />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="ic_futuro_pj">IC Futuro PJ:</label>
                  <div className="whitelist-input-group">
                    <textarea
                      id="ic_futuro_pj"
                      name="ic_futuro_pj"
                      value={newRequest.ic_futuro_pj}
                      onChange={handleNewRequestInputChange}
                      rows="3"
                      required
                    />
                    <FaCompass className="whitelist-input-icon textarea-icon" />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="ic_historia_pj">IC Historia PJ:</label>
                  <div className="whitelist-input-group">
                    <textarea
                      id="ic_historia_pj"
                      name="ic_historia_pj"
                      value={newRequest.ic_historia_pj}
                      onChange={handleNewRequestInputChange}
                      rows="3"
                      required
                    />
                    <FaHistory className="whitelist-input-icon textarea-icon" />
                  </div>
                </div>

                <div className="form-actions" style={{ marginTop: 20 }}>
                  <button type="submit" className="action-button primary-button">
                    Enviar Solicitud
                  </button>
                  <button
                    type="button"
                    className="action-button secondary-button"
                    onClick={() => setIsAddWhitelistModalOpen(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL AÑADIDO: Para la vista de detalles de la solicitud */}
        {isViewModalOpen && selectedRequest && (
          <div className="modal-overlay" onClick={() => setIsViewModalOpen(false)}>
            <div className="modal-content whitelist-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <h2 className="modal-title" style={{margin:0,fontSize:'1.4rem'}}>Detalles de Solicitud</h2>
                  <div className="modal-subtitle">Usuario: <b>{selectedRequest.username}</b> · ID: {selectedRequest.id}</div>
                </div>
                <button className="modal-close-button fancy-close" onClick={() => setIsViewModalOpen(false)} aria-label="Cerrar">
                  <FaTimes />
                </button>
              </div>
              <div className="details-container">
                <div className="section-title" style={{gridColumn:'1 / -1'}}><span className="section-icon"><FaInfoCircle /></span> Información General</div>
                <DetailItem label="ID" value={selectedRequest.id} />
                <DetailItem label="Usuario" value={selectedRequest.username} />
                <DetailItem label="Discord ID" value={selectedRequest.discord_id} />
                <DetailItem label="Estado" value={<span className={`status-badge status-${selectedRequest.status}`}>{STATUS_LABELS[selectedRequest.status] || selectedRequest.status}</span>} />
                <DetailItem label="Fecha de Envío" value={selectedRequest.created_at ? new Date(selectedRequest.created_at).toLocaleString('es-ES', { hour12: false }) : 'N/D'} />
                <DetailItem label="Última Actualización" value={selectedRequest.updated_at ? new Date(selectedRequest.updated_at).toLocaleString('es-ES', { hour12: false }) : 'N/D'} />
                <div className="section-title" style={{gridColumn:'1 / -1', marginTop:10}}><span className="section-icon"><FaUser /></span> Información OOC (Out Of Character)</div>
                <DetailItem label="Edad OOC" value={selectedRequest.ooc_edad} />
                <DetailItem label="Experiencia Rol OOC" value={<span className="detail-value">{selectedRequest.ooc_experiencia_rol}</span>} isBlock={true} />
                <div className="section-title" style={{gridColumn:'1 / -1', marginTop:10}}><span className="section-icon"><FaBook /></span> Información IC (In Character)</div>
                <DetailItem label="Aptitudes PJ" value={<span className="detail-value">{selectedRequest.ic_aptitudes_pj}</span>} isBlock={true} />
                <DetailItem label="Futuro PJ" value={<span className="detail-value">{selectedRequest.ic_futuro_pj}</span>} isBlock={true} />
                <DetailItem label="Historia PJ" value={<span className="detail-value">{selectedRequest.ic_historia_pj}</span>} isBlock={true} />
              </div>
              <div className="modal-actions">
                <button type="button" className="link-button" onClick={() => setIsViewModalOpen(false)}>Cerrar</button>
                <div className="actions-right">
                  <button onClick={() => updateRequestStatus(selectedRequest.id, 'approved')} className="action-button icon-button success-button" title="Aprobar">
                    <FaCheckCircle style={{marginRight:6}} /> Aprobar
                  </button>
                  <button onClick={() => updateRequestStatus(selectedRequest.id, 'rejected')} className="action-button icon-button danger-button" title="Rechazar">
                    <FaTimesCircle style={{marginRight:6}} /> Rechazar
                  </button>
                  <button onClick={() => handleDeleteRequest(selectedRequest.id)} className="action-button icon-button delete-button" title="Eliminar">
                    <FaTrash style={{marginRight:6}} /> Eliminar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWhitelist;